import {ScryfallCard} from "@scryfall/api-types";
import {useRouter} from "next/router";

import {buttonMap} from "@/lib/constants";
import {useSpellifyState} from "@/pages/spellify";
import {buttonMapType} from "@/types/spellify/ButtonMapType";
import {GameStatsType} from "@/types/spellify/stats";

export type SpellifyMode = 'normal' | 'hard' | 'salt'
export type SpellifyGameState = 'guessing' | 'success' | 'failure'

export const DEFAULT_GAME_STATS: GameStatsType = {
  'spellifyNumber': 0,
  'gamesPlayed': 0,
  'gamesWon': 0,
  'currentStreak': 0,
  'maxStreak': 0,
  'winRate': 0,
  'recentGuessNumber': 0,
  'guessList': [],
  'guessHistory': {'0':0, '1':0, '2':0, '3':0, '4':0, '5':0, '6':0, '7':0, '8':0, '9':0, '10':0, '11':0, '12':0}
}

// What each mode calls itself in headings and share messages, and where it lives
export const MODE_LABELS: Record<SpellifyMode, string> = {normal: '', hard: 'Hard Mode', salt: 'Salt'}
export const MODE_PATHS: Record<SpellifyMode, string> = {normal: '/spellify', hard: '/spellify/hard', salt: '/spellify/salt'}

export const getSpellifyMode = (path: string): SpellifyMode =>
  path.includes('salt') ? 'salt' : path.includes('hard') ? 'hard' : 'normal'

// Salt mode plays five cards a day, so each of its values is stored per card index
const replaceAt = <T,>(list: T[], index: number, value: T): T[] => {
  const next = [...list]
  next[index] = value
  return next
}

// The board the player is currently looking at, whichever mode they're in
type ModeBoard = {
  card: ScryfallCard.Normal
  puzzleNumber: number
  buttonState: buttonMapType
  setButtonState: (value: buttonMapType) => void
  guesses: string[]
  setGuesses: (value: string[]) => void
  cardGuesses: string[][]
  setCardGuesses: (value: string[][]) => void
  gameState: SpellifyGameState
  setGameState: (value: SpellifyGameState) => void
  gameStats: GameStatsType
  setGameStats: (value: GameStatsType) => void
  playedIncremented: boolean
  setPlayedIncremented: (value: boolean) => void
}

type Props = {
  dailyCard: ScryfallCard.Normal
  hardDailyCard: ScryfallCard.Normal
  spellifyNumber: number
  hardSpellifyNumber: number
  saltCards?: ScryfallCard.Normal[] | null
  saltNumber?: number
}

export type SpellifyModeContext = ModeBoard & {
  mode: SpellifyMode
  label: string
  isHardMode: boolean
  isSaltMode: boolean
  isInfiniteMode: boolean
  // True when the stored progress is from an older puzzle and needs wiping
  isNewDay: boolean
  saltIndex: number
  saltCardCount: number
  hasNextSaltCard: boolean
  // The card this mode starts on once a new day has been reset
  newDayCard: ScryfallCard.Normal
  goToNextSaltCard: () => void
  resetForNewDay: () => void
  // Infinite mode and admins swap in a random card without waiting for tomorrow
  replaceDailyCard: (card: ScryfallCard.Normal) => void
}

// Owns every piece of state that differs between normal, hard and salt mode, so the game itself
// only ever deals with one board at a time.
const useSpellifyMode = ({dailyCard, hardDailyCard, spellifyNumber, hardSpellifyNumber, saltCards, saltNumber}: Props): SpellifyModeContext => {
  const router = useRouter()
  const mode = getSpellifyMode(router.asPath)
  const isInfiniteMode = router.asPath.includes('infinite')

  const [normalCard, setNormalCard] = useSpellifyState('spellifyCard', dailyCard)
  const [hardCard, setHardCard] = useSpellifyState('spellifyHardCard', hardDailyCard)

  const [buttonStateNormal, setButtonStateNormal] = useSpellifyState('spellifyButtonMap', buttonMap)
  const [buttonStateHard, setButtonStateHard] = useSpellifyState('spellifyButtonMapHard', buttonMap)

  const [guessesNormal, setGuessesNormal] = useSpellifyState('spellifyGuesses', [])
  const [guessesHard, setGuessesHard] = useSpellifyState('spellifyGuessesHard', [])

  const [cardGuessesNormal, setCardGuessesNormal] = useSpellifyState('spellifyCardGuesses', [])
  const [cardGuessesHard, setCardGuessesHard] = useSpellifyState('spellifyCardGuessesHard', [])

  const [gameStateNormal, setGameStateNormal] = useSpellifyState('spellifyGameState', 'guessing')
  const [gameStateHard, setGameStateHard] = useSpellifyState('spellifyGameStateHard', 'guessing')

  const [gameStatsNormal, setGameStatsNormal] = useSpellifyState('spellifyGameStats', DEFAULT_GAME_STATS)
  const [gameStatsHard, setGameStatsHard] = useSpellifyState('spellifyGameStatsHard', DEFAULT_GAME_STATS)

  const [playedNormal, setPlayedNormal] = useSpellifyState('spellifyPlayedToday', false)
  const [playedHard, setPlayedHard] = useSpellifyState('spellifyPlayedTodayHard', false)

  const [localSpellifyNumber, setLocalSpellifyNumber] = useSpellifyState('spellifyNumber', 0)

  const [saltDay, setSaltDay] = useSpellifyState('spellifySaltNumber', 0)
  const [storedSaltIndex, setSaltIndex] = useSpellifyState('spellifySaltIndex', 0)
  const [saltButtonStates, setSaltButtonStates] = useSpellifyState('spellifySaltButtonMaps', [])
  const [saltGuesses, setSaltGuesses] = useSpellifyState('spellifySaltGuesses', [])
  const [saltCardGuesses, setSaltCardGuesses] = useSpellifyState('spellifySaltCardGuesses', [])
  const [saltGameStates, setSaltGameStates] = useSpellifyState('spellifySaltGameStates', [])
  const [saltPlayed, setSaltPlayed] = useSpellifyState('spellifySaltPlayed', [])
  const [saltGameStats, setSaltGameStats] = useSpellifyState('spellifySaltGameStats', DEFAULT_GAME_STATS)

  const saltCardList = saltCards || []
  const saltCardCount = saltCardList.length
  const saltIndex = Math.min(storedSaltIndex || 0, Math.max(saltCardCount - 1, 0))

  const boards: Record<SpellifyMode, ModeBoard> = {
    normal: {
      card: normalCard?.name ? normalCard : dailyCard,
      puzzleNumber: spellifyNumber,
      buttonState: buttonStateNormal || buttonMap,
      setButtonState: setButtonStateNormal,
      guesses: guessesNormal || [],
      setGuesses: setGuessesNormal,
      cardGuesses: cardGuessesNormal || [],
      setCardGuesses: setCardGuessesNormal,
      gameState: gameStateNormal || 'guessing',
      setGameState: setGameStateNormal,
      gameStats: gameStatsNormal || DEFAULT_GAME_STATS,
      setGameStats: setGameStatsNormal,
      playedIncremented: !!playedNormal,
      setPlayedIncremented: setPlayedNormal,
    },
    hard: {
      card: hardCard?.name ? hardCard : hardDailyCard,
      puzzleNumber: hardSpellifyNumber,
      buttonState: buttonStateHard || buttonMap,
      setButtonState: setButtonStateHard,
      guesses: guessesHard || [],
      setGuesses: setGuessesHard,
      cardGuesses: cardGuessesHard || [],
      setCardGuesses: setCardGuessesHard,
      gameState: gameStateHard || 'guessing',
      setGameState: setGameStateHard,
      gameStats: gameStatsHard || DEFAULT_GAME_STATS,
      setGameStats: setGameStatsHard,
      playedIncremented: !!playedHard,
      setPlayedIncremented: setPlayedHard,
    },
    salt: {
      // Falls back to the daily card if the salt cards failed to load, so the board still renders
      card: saltCardList[saltIndex] || dailyCard,
      puzzleNumber: saltNumber || 0,
      buttonState: (saltButtonStates || [])[saltIndex] || buttonMap,
      setButtonState: (value) => setSaltButtonStates(replaceAt(saltButtonStates || [], saltIndex, value)),
      guesses: (saltGuesses || [])[saltIndex] || [],
      setGuesses: (value) => setSaltGuesses(replaceAt(saltGuesses || [], saltIndex, value)),
      cardGuesses: (saltCardGuesses || [])[saltIndex] || [],
      setCardGuesses: (value) => setSaltCardGuesses(replaceAt(saltCardGuesses || [], saltIndex, value)),
      gameState: (saltGameStates || [])[saltIndex] || 'guessing',
      setGameState: (value) => setSaltGameStates(replaceAt(saltGameStates || [], saltIndex, value)),
      // Salt keeps its own stats so playing it can't break the daily streak
      gameStats: saltGameStats || DEFAULT_GAME_STATS,
      setGameStats: setSaltGameStats,
      playedIncremented: !!(saltPlayed || [])[saltIndex],
      setPlayedIncremented: (value) => setSaltPlayed(replaceAt(saltPlayed || [], saltIndex, value)),
    },
  }

  const resetDailyCards = (normal: ScryfallCard.Normal, hard: ScryfallCard.Normal) => {
    setLocalSpellifyNumber(spellifyNumber)
    setGuessesNormal([])
    setGuessesHard([])
    setButtonStateNormal(buttonMap)
    setButtonStateHard(buttonMap)
    setCardGuessesNormal([])
    setCardGuessesHard([])
    setNormalCard(normal)
    setHardCard(hard)
    setGameStateNormal('guessing')
    setGameStateHard('guessing')
  }

  const resetSalt = () => {
    setSaltDay(saltNumber || 0)
    setSaltIndex(0)
    setSaltButtonStates([])
    setSaltGuesses([])
    setSaltCardGuesses([])
    setSaltGameStates([])
    setSaltPlayed([])
  }

  const hasNextSaltCard = mode === 'salt' && saltIndex < saltCardCount - 1

  return {
    ...boards[mode],
    mode,
    label: MODE_LABELS[mode],
    isHardMode: mode === 'hard',
    isSaltMode: mode === 'salt',
    isInfiniteMode,
    isNewDay: mode === 'salt' ? saltDay !== saltNumber : localSpellifyNumber !== spellifyNumber,
    saltIndex,
    saltCardCount,
    hasNextSaltCard,
    newDayCard: mode === 'salt' ? (saltCardList[0] || dailyCard) : mode === 'hard' ? hardDailyCard : dailyCard,
    goToNextSaltCard: () => { if (hasNextSaltCard) setSaltIndex(saltIndex + 1) },
    resetForNewDay: () => mode === 'salt' ? resetSalt() : resetDailyCards(dailyCard, hardDailyCard),
    replaceDailyCard: (card: ScryfallCard.Normal) => mode === 'hard'
      ? resetDailyCards(dailyCard, card)
      : resetDailyCards(card, hardDailyCard),
  }
}

export default useSpellifyMode
